import type { FieldValue, GravityFormField } from './types';

/** Matches Sonic's 30s submit timeout. */
const TIMEOUT = 30000;

/** GF NAME sub-input id suffix → `nameValues` key. */
const NAME_KEY_BY_SUFFIX: Record<
  string,
  keyof NonNullable<FieldValue['nameValues']>
> = {
  '2': 'prefix',
  '3': 'first',
  '4': 'middle',
  '6': 'last',
  '8': 'suffix',
};

/** Field input name for a whole field: `input_{formId}_{fieldId}`. */
export const fieldName = (formId: number, fieldId: number): string =>
  `input_${formId}_${fieldId}`;

/** Sub-input name for composite/choice fields: `input_{formId}_{inputId}`. */
export const subInputName = (formId: number, inputId: string): string =>
  `input_${formId}_${inputId}`;

const asString = (value: FormDataEntryValue | null): string =>
  typeof value === 'string' ? value : '';

/**
 * Builds the typed `FormFieldValuesInput[]` payload from submitted form data,
 * driven by the form's field metadata (not DOM scraping) so it is deterministic
 * and unit-testable.
 *
 * Extends the legacy Sonic helper (which only produced email/file/consent/plain
 * shapes) with the variants the mutation requires for the new field types:
 * CHECKBOX → `checkboxValues`, NAME → `nameValues`, SELECT/NUMBER/DATE → `value`.
 */
export const getFieldValues = (
  formData: FormData,
  fields: GravityFormField[],
  formId: number,
): FieldValue[] => {
  const values: FieldValue[] = [];

  for (const field of fields) {
    const base = fieldName(formId, field.id);

    switch (field.type) {
      case 'EMAIL': {
        const value = asString(formData.get(base));
        if (value) values.push({ id: field.id, emailValues: { value } });
        break;
      }
      case 'FILEUPLOAD': {
        const files = formData
          .getAll(base)
          .filter((v): v is File => v instanceof File && v.size > 0);
        if (files.length)
          values.push({ id: field.id, fileUploadValues: files });
        break;
      }
      case 'CONSENT': {
        const checked = formData.get(base) != null;
        values.push({ id: field.id, value: String(checked) });
        break;
      }
      case 'CHECKBOX': {
        const inputs =
          field.inputs ??
          field.choices?.map((_choice, index) => ({
            id: `${field.id}.${index + 1}`,
          })) ??
          [];
        const checkboxValues = inputs
          .map((input) => {
            const raw = asString(formData.get(subInputName(formId, input.id)));
            return raw ? { inputId: Number(input.id), value: raw } : null;
          })
          .filter((entry): entry is { inputId: number; value: string } =>
            Boolean(entry),
          );
        if (checkboxValues.length)
          values.push({ id: field.id, checkboxValues });
        break;
      }
      case 'NAME': {
        const nameValues: NonNullable<FieldValue['nameValues']> = {};
        (field.inputs ?? []).forEach((input) => {
          if (input.isHidden) return;
          const suffix = input.id.split('.')[1];
          const key = suffix ? NAME_KEY_BY_SUFFIX[suffix] : undefined;
          const raw = asString(formData.get(subInputName(formId, input.id)));
          if (key && raw) nameValues[key] = raw;
        });
        if (Object.keys(nameValues).length)
          values.push({ id: field.id, nameValues });
        break;
      }
      default: {
        const value = asString(formData.get(base));
        if (value) values.push({ id: field.id, value });
      }
    }
  }

  return values;
};

export interface TagularFieldData {
  fieldId: string;
  fieldLabel: string;
  fieldName: string;
  fieldType: string;
  fieldValue: 'hidden' | 'blank';
}

/**
 * True when any submitted entry for `base` is non-empty, including composite
 * sub-inputs (CHECKBOX/NAME post under `input_{formId}_{fieldId}.{n}`), so
 * filled composite fields aren't reported as `blank`.
 */
const fieldWasFilled = (formData: FormData, base: string): boolean => {
  let filled = false;
  formData.forEach((value, key) => {
    if (filled) return;
    if (key !== base && !key.startsWith(`${base}.`)) return;
    if (value instanceof File ? value.size > 0 : asString(value).length > 0) {
      filled = true;
    }
  });
  return filled;
};

/** Builds Tagular `field` context for the `FormSubmitted` event (values are never sent raw). */
export const getTagularFieldData = (
  fields: GravityFormField[],
  formData: FormData,
  formId: number,
): TagularFieldData[] =>
  fields.map((field) => {
    const name = fieldName(formId, field.id);
    const hasValue = fieldWasFilled(formData, name);
    return {
      fieldId: String(field.id),
      fieldLabel: field.label ?? '',
      fieldName: name,
      fieldType: field.type,
      fieldValue: hasValue ? 'hidden' : 'blank',
    };
  });

interface SubmitResult {
  data: { errors?: { id: number; message: string }[] } | null;
  errors?: { message: string }[];
}

const parseSubmitResponse = async (
  response: Response,
): Promise<SubmitResult> => {
  if (!response.ok) {
    throw new Error(
      `Failed to submit form with code ${response.status}: ${response.statusText}`,
    );
  }
  const json = await response.json();
  const submitGfForm = json?.data?.submitGfForm ?? null;
  return { data: submitGfForm, errors: json?.errors };
};

const sendJsonRequest = async (
  cmsUrl: string,
  query: string,
  id: number,
  fieldValues: FieldValue[],
): Promise<SubmitResult> => {
  const response = await fetch(`${cmsUrl}/graphql`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, variables: { id, fieldValues } }),
    signal: AbortSignal.timeout(TIMEOUT),
  });
  return parseSubmitResponse(response);
};

const sendMultipartRequest = async (
  cmsUrl: string,
  query: string,
  id: number,
  fieldValues: FieldValue[],
): Promise<SubmitResult> => {
  const formData = new FormData();
  const operations = {
    query,
    variables: {
      id,
      fieldValues: fieldValues.map((field) => ({
        ...field,
        fileUploadValues: field.fileUploadValues || [],
      })),
    },
  };
  formData.append('operations', JSON.stringify(operations));

  const fileMap: Record<string, string[]> = {};
  fieldValues.forEach((field, fieldIndex) => {
    const files = field.fileUploadValues;
    if (files instanceof FileList || Array.isArray(files)) {
      Array.from(files).forEach((file, fileIndex) => {
        const fileKey = `file_${fieldIndex}_${fileIndex}`;
        formData.append(fileKey, file);
        fileMap[fileKey] = [
          `variables.fieldValues.${fieldIndex}.fileUploadValues.${fileIndex}`,
        ];
      });
    }
  });
  formData.append('map', JSON.stringify(fileMap));

  const response = await fetch(`${cmsUrl}/graphql`, {
    method: 'POST',
    body: formData,
    signal: AbortSignal.timeout(TIMEOUT),
  });
  return parseSubmitResponse(response);
};

/** Posts the form to `{cmsUrl}/graphql`; uses GraphQL multipart when files are present. */
export const sendFormRequest = async (
  cmsUrl: string,
  query: string,
  id: number,
  fieldValues: FieldValue[],
): Promise<SubmitResult> => {
  const hasFile = fieldValues.some((field) => Boolean(field.fileUploadValues));
  return hasFile
    ? sendMultipartRequest(cmsUrl, query, id, fieldValues)
    : sendJsonRequest(cmsUrl, query, id, fieldValues);
};
